/**
 * VerifyEmailPage.tsx — MER email-address verification. Phase-2 P2-D5.
 *
 * ── The token arrives in the FRAGMENT ────────────────────────────────────────
 * The link is /verify-email#token=… and the '#' is load-bearing. A fragment is
 * never transmitted to a server, so the token cannot reach an access log, a
 * proxy, or a platform request-path store (D-P2AC-6). The query-string form this
 * replaced was found writing a bearer token verbatim into production request
 * logs on the P2-D3 flow.
 *
 * ── Why confirming on load is safe HERE ──────────────────────────────────────
 * Mail-security systems prefetch links, which is exactly why P2-D3's validation
 * had to be non-consuming. A prefetcher following THIS link sends only
 * /verify-email — the fragment stays in its own client — so it cannot carry a
 * token and cannot consume one. The fragment transport is what buys the
 * one-click confirmation; it is not an incidental detail.
 *
 * ── The token is held in memory and nowhere else ─────────────────────────────
 * Read once into a ref, fragment stripped immediately with replaceState, never
 * written to localStorage or sessionStorage, and submitted only in a POST body.
 *
 * ── This page grants nothing ─────────────────────────────────────────────────
 * Verification is recorded identity assurance (D-P2AC-1). It does not sign the
 * user in, change any credential, or unlock anything. There is deliberately no
 * session side effect here at all.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { csrfHeader } from '../../auth/AuthContext';

type Phase = 'checking' | 'done' | 'already' | 'invalid' | 'unavailable';

const SHELL: React.CSSProperties = {
  background: '#080F1C',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: "'Courier New', Courier, monospace",
  backgroundImage: `linear-gradient(rgba(26,48,80,0.15) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(26,48,80,0.15) 1px, transparent 1px)`,
  backgroundSize: '40px 40px',
};

export function VerifyEmailPage() {
  const tokenRef = useRef<string>('');
  const [phase, setPhase] = useState<Phase>('checking');

  // Referrer-Policy as a document-level meta. The header form would have to be
  // set in server.js, which is byte-pinned by a banked contract test. With the
  // token in the fragment it can never appear in a Referer anyway, so this is
  // defence in depth rather than the control.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    const hash  = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const token = new URLSearchParams(hash).get('token') ?? '';
    tokenRef.current = token;

    // Strip the token from the visible URL and the history entry immediately.
    if (token) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!token) { setPhase('invalid'); return; }

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/auth/email-verification/confirm', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        if (r.status === 503) { setPhase('unavailable'); return; }
        const body = await r.json().catch(() => ({}));
        if (r.ok && body?.ok) { setPhase(body.alreadyVerified ? 'already' : 'done'); return; }
        setPhase('invalid');
      } catch {
        if (!cancelled) setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mer-full-page" style={SHELL}>
      <div style={{ width: 380, padding: 28, background: '#0A1628', border: '1px solid #1A3050', borderRadius: 4 }}>
        <div style={{ fontSize: 11, letterSpacing: 3, color: '#C9A84C', marginBottom: 4 }}>MER ALGO FOOTPRINTS</div>
        <div style={{ fontSize: 10, letterSpacing: 1, color: '#3A5070', marginBottom: 22 }}>EMAIL VERIFICATION</div>

        {phase === 'checking' && (
          <div style={{ fontSize: 12, color: '#3A5070' }}>Verifying…</div>
        )}

        {phase === 'done' && (
          <div style={{ fontSize: 12, color: '#6FCF97', lineHeight: 1.7 }}>
            Your email address is verified. Nothing else about your account has
            changed.
          </div>
        )}

        {phase === 'already' && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            This email address was already verified. No further action is needed.
          </div>
        )}

        {phase === 'invalid' && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            This verification link is no longer valid. Verification links expire
            and can only be used once. You can request a new one from the
            Security page after signing in.
          </div>
        )}

        {phase === 'unavailable' && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            Email verification is not currently available. Please try again later.
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 22, fontSize: 11, color: '#3A5070' }}>
          <Link to="/login" style={{ color: '#3A5070', textDecoration: 'none' }}>← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
